import { SkyButton, SkyLink, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { absolutePath } from "@/lib/routes";
import { useLayoutEffect, useRef, useState, type ReactElement } from "react";
import type { ImportCauseView } from "@shared/types/importCauses";
import { fixBeforeRetry } from "@shared/types/whoActs";
import type { RunDetail, RunStepRow } from "@shared/types/runs";
import { RetryIcon } from "@/lib/icons";
import { wasForceReset } from "./retryTargets";
import { FORCE_RESET_ERROR_PREFIX } from "@shared/runs/forceReset";
import { namesItsConnection, rowsIntoTables, type RunActivity } from "@/lib/runs/runActivity";
import { ImportCauseBlock, type ImportCauseHandlers } from "./ImportCauseBlock";
import { partlyFailedSentence } from "@shared/runs/partlyFailed";
import { formatDuration, runPartlyFailedFacts, transformLineParts } from "./runFormatting";

/**
 * What happened to this import, in one sentence at the top of the run page (Session 10, P11;
 * ux-s10.md §1): the outcome callout.
 *
 * One `SkyMessageBar layout="multiline"` whose intent and sentence follow the outcome:
 * queued "Waiting to start: {q} imports ahead of this one."; running "Importing: step {n} of
 * {m}, {label}."; succeeded "Import finished in {d}."; partial "Partly failed: {k} of {n}
 * connections failed ({names})." (#817, `shared/runs/partlyFailed.ts`); failed "Import failed at step {n} of {m}: {label}." with the step's
 * sanitised error; cancelled "Import cancelled.". A failed import offers "Retry from {step}" to a
 * reader with `runs.retry` only, opening the page's existing retry dialog.
 *
 * **It does not announce** (S2): the library hard-codes `role` and `aria-live` on the bar by
 * intent, and the page already has one polite line that says each step once. The bar's live
 * attributes are removed through its ref before paint, the pattern P13a's notice region uses, so
 * a screen reader hears the step once, not twice.
 */

export interface OutcomeCopy {
  intent: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
}

/** ", attempt 2" only on a retry. */
function attemptPart(activity: RunActivity): string {
  return activity.attempt !== null && activity.attempt > 1 ? `, attempt ${activity.attempt}` : "";
}

/**
 * " (HR)" on a run of several connections, for a step that belongs to one of them: the label says
 * "Extract from the source" there, so the callout (and the page's one polite line, which reads its
 * title) names the connection the way the timeline and the step details do. A run of one
 * connection already names it in the label; a run-level step (transform, finalize) has none.
 */
function connectionSuffix(
  activity: RunActivity,
  step: RunActivity["steps"][number] | undefined,
): string {
  if (!step?.dataflowName || step.dataflowId === null || namesItsConnection(step)) return "";
  const connections = new Set(
    activity.steps.flatMap((entry) => (entry.dataflowId === null ? [] : [entry.dataflowId])),
  );
  return connections.size > 1 ? ` (${step.dataflowName})` : "";
}

/**
 * The step the running sentence is about, by the same rule `deriveRunActivity` picks it: the
 * running row, else the first pending one.
 */
function currentStep(activity: RunActivity): RunActivity["steps"][number] | undefined {
  return (
    activity.steps.find((step) => step.status === "running") ??
    activity.steps.find((step) => step.status === "pending")
  );
}

/** The first sanitised error a step of this run recorded: the cause a partial or failed run shows. */
function firstStepError(run: RunDetail): string | null {
  return run.steps.find((step) => step.error)?.error ?? null;
}

/** The step order a run's chain follows; the first failed step in it names the cause. */
const CHAIN: readonly RunStepRow["step"][] = ["extract", "load", "merge", "transform", "finalize"];

/**
 * The cause the callout shows (product gaps row 4): on a failed import, the cause of the failed
 * step the title names, else of the first failed step in chain order that has one; on a partial
 * import, the first step's that has one. Null in every other phase, or when no step has one.
 */
export function calloutCause(
  activity: RunActivity,
  run: RunDetail,
): { cause: ImportCauseView; dataflowId: string | null } | null {
  if (activity.phase !== "failed" && activity.phase !== "partial") return null;
  const withCause = run.steps.filter((row): row is RunStepRow & { cause: ImportCauseView } =>
    Boolean(row.cause),
  );
  const ordered = [...withCause].sort((a, b) => CHAIN.indexOf(a.step) - CHAIN.indexOf(b.step));
  if (activity.phase === "failed") {
    const named =
      activity.failedStepIndex !== null ? activity.steps[activity.failedStepIndex - 1] : undefined;
    const exact = named
      ? ordered.find((row) => row.step === named.step && row.dataflowId === named.dataflowId)
      : undefined;
    const row = exact ?? ordered.find((candidate) => candidate.status === "failed");
    return row ? { cause: row.cause, dataflowId: row.dataflowId } : null;
  }
  const row = ordered[0];
  return row ? { cause: row.cause, dataflowId: row.dataflowId } : null;
}

/**
 * Round 6, W5: a run with no connection (a transform-only schedule, or Run on Transformations) is
 * not an import, so it is named for what it is: "Transformation run failed at step 1 of 2".
 */
export function isTransformOnlyRun(run: Pick<RunDetail, "dataflowIds" | "steps">): boolean {
  return run.dataflowIds.length === 0 && run.steps.some((step) => step.step === "transform");
}

function runningImportCopy(activity: RunActivity): OutcomeCopy {
  let title = "Importing.";
  if (activity.stepIndex !== null && activity.stepTotal !== null) {
    const label = activity.stepLabel
      ? `, ${activity.stepLabel}${connectionSuffix(activity, currentStep(activity))}`
      : "";
    title = `Importing: step ${activity.stepIndex} of ${activity.stepTotal}${label}${attemptPart(activity)}.`;
  }
  return {
    intent: "info",
    title,
    message: activity.sentence ?? "The page follows the import as it moves.",
  };
}

function succeededImportCopy(activity: RunActivity, run: RunDetail): OutcomeCopy {
  const title =
    activity.durationSeconds !== null
      ? `Import finished in ${formatDuration(activity.durationSeconds)}.`
      : "Import finished.";
  return { intent: "success", title, message: succeededMessage(activity, run) };
}

/** Rows into tables, else the transformations' line, else that every step succeeded. */
function succeededMessage(activity: RunActivity, run: RunDetail): string {
  // #2392: "21,042 rows loaded into 12 tables, 8 had no rows."
  const into = rowsIntoTables(activity);
  if (into) return `${into.replace(" into ", " loaded into ")}.`;
  const transform = transformLineParts(run.transformTotals);
  if (transform.length > 0) return `${transform.map((part) => part.text).join(" · ")}.`;
  return "Every step succeeded.";
}

function partialImportCopy(run: RunDetail): OutcomeCopy {
  return {
    intent: "warning",
    // #817: the connections that failed, the ones Connections reads Failed.
    title: `${partlyFailedSentence(runPartlyFailedFacts(run, undefined, run.steps))}.`,
    // The title already says what failed; the default only points to where to look.
    message: firstStepError(run) ?? "The Tables view says which tables did not load.",
  };
}

function failedImportCopy(activity: RunActivity, run: RunDetail): OutcomeCopy {
  let title = "Import failed.";
  if (activity.failedStepIndex !== null && activity.stepTotal !== null) {
    const where = activity.failedStepLabel
      ? `${activity.failedStepLabel}${connectionSuffix(
          activity,
          activity.steps[activity.failedStepIndex - 1],
        )}`
      : "a step";
    title = `Import failed at step ${activity.failedStepIndex} of ${activity.stepTotal}: ${where}.`;
  }
  return {
    intent: "error",
    title,
    // Verbatim: the runner already reduced this text (SEC-17); there is no longer version.
    message: activity.cause ?? firstStepError(run) ?? "The step recorded no error text.",
  };
}

/** `text` without its closing run of `.`, `!` and `?`, in one backward scan (#1411, Sonar S5852). */
function withoutClosingPunctuation(text: string): string {
  let end = text.length;
  while (end > 0 && ".!?".includes(text.charAt(end - 1))) end -= 1;
  return text.slice(0, end);
}

/**
 * The reason a force reset recorded, without its closing full stop: the run's summary first, else
 * the first step it cancelled (`Force reset: <reason>`), because finalize REPLACES `runs.summary`
 * and a force reset stops no container, so a late finalize can drop the summary's key while the
 * step rows keep the reason. Both copies come through the server's run-text reader.
 */
function forceResetReason(run: Pick<RunDetail, "summary" | "steps">): string | null {
  const tidy = (value: unknown): string | null =>
    typeof value === "string" && value.trim() ? withoutClosingPunctuation(value.trim()) : null;
  const summary = run.summary;
  if (summary !== null && typeof summary === "object" && "forceReset" in summary) {
    const reset = (summary as { forceReset?: unknown }).forceReset;
    const reason = tidy(
      reset !== null && typeof reset === "object" ? (reset as { reason?: unknown }).reason : null,
    );
    if (reason) return reason;
  }
  const step = run.steps.find(
    (row) => row.status === "cancelled" && row.error?.startsWith(FORCE_RESET_ERROR_PREFIX),
  );
  return tidy(step?.error?.slice(FORCE_RESET_ERROR_PREFIX.length));
}

/**
 * #1231: a force reset lands a run Cancelled (`shared/runs/forceReset.ts`), and its steps read
 * Cancelled, so the callout says it was a force reset and the reason given, not "failed".
 */
function forceResetCopy(run: RunDetail, noun: "Import" | "Transformation run"): OutcomeCopy | null {
  if (!wasForceReset(run, run.steps)) return null;
  const reason = forceResetReason(run);
  return {
    intent: "info",
    title: `${noun} cancelled by a force reset.`,
    message: reason
      ? `Reason given: ${reason}. Nothing more runs for this ${noun === "Import" ? "import" : "run"}.`
      : `Nothing more runs for this ${noun === "Import" ? "import" : "run"}.`,
  };
}

/** The callout's words for an outcome. Pure, so every outcome is tested without a screen. */
export function outcomeCopy(activity: RunActivity, run: RunDetail): OutcomeCopy {
  if (isTransformOnlyRun(run)) return transformRunCopy(activity, run);
  switch (activity.phase) {
    case "queued":
      return {
        intent: "info",
        title: activity.queuedReason ?? "Waiting to start.",
        message: "The import is queued. Skypoint starts it as soon as a slot is free.",
      };
    case "running":
      return runningImportCopy(activity);
    case "succeeded":
      return succeededImportCopy(activity, run);
    case "partial":
      return partialImportCopy(run);
    case "failed":
      return failedImportCopy(activity, run);
    case "cancelled":
      return (
        forceResetCopy(run, "Import") ?? {
          intent: "info",
          title: "Import cancelled.",
          message: "Nothing more runs for this import. Start a new import to try again.",
        }
      );
    default:
      return { intent: "info", title: "Loading this import…", message: "" };
  }
}

/** A running transformation run's title: the step it is on (and its name), else just dbt. */
function runningDbtTitle(activity: RunActivity): string {
  if (activity.stepIndex === null || activity.stepTotal === null) return "Running dbt.";
  const label = activity.stepLabel ? `, ${activity.stepLabel}` : "";
  return `Running dbt: step ${activity.stepIndex} of ${activity.stepTotal}${label}${attemptPart(activity)}.`;
}

/** W5: the same outcomes for a run that imports nothing and runs transformations. */
function transformRunCopy(activity: RunActivity, run: RunDetail): OutcomeCopy {
  const transform = transformLineParts(run.transformTotals);
  switch (activity.phase) {
    case "queued":
      return {
        intent: "info",
        title: activity.queuedReason ?? "Waiting to start.",
        message: "The transformation run is queued. Skypoint starts it as soon as a slot is free.",
      };
    case "running":
      return {
        intent: "info",
        title: runningDbtTitle(activity),
        message: activity.sentence ?? "The page follows the run as it moves.",
      };
    case "succeeded":
      return {
        intent: "success",
        title:
          activity.durationSeconds !== null
            ? `Transformation run finished in ${formatDuration(activity.durationSeconds)}.`
            : "Transformation run finished.",
        message:
          transform.length > 0
            ? `${transform.map((part) => part.text).join(" · ")}.`
            : "Every step succeeded.",
      };
    case "partial":
      return {
        intent: "warning",
        title: "Transformation run partly failed.",
        message:
          firstStepError(run) ?? "Some models did not build. The dbt results view says which.",
      };
    case "failed":
      return {
        intent: "error",
        title:
          activity.failedStepIndex !== null && activity.stepTotal !== null
            ? `Transformation run failed at step ${activity.failedStepIndex} of ${activity.stepTotal}: ${
                activity.failedStepLabel ?? "a step"
              }.`
            : "Transformation run failed.",
        message: activity.cause ?? firstStepError(run) ?? "The step recorded no error text.",
      };
    case "cancelled":
      return (
        forceResetCopy(run, "Transformation run") ?? {
          intent: "info",
          title: "Transformation run cancelled.",
          message: "Nothing more runs for this run. Start a new run to try again.",
        }
      );
    default:
      return { intent: "info", title: "Loading this run…", message: "" };
  }
}

/**
 * The page's one polite sentence for this moment: the callout's title, except while queued. The
 * queued title carries the count of imports ahead, which moves while nothing about this import
 * does, and the line would say it again on every change (ux §1: once per step transition). So a
 * queued import is announced as "Waiting to start." once, and the count stays on screen.
 */
export function outcomeAnnouncement(activity: RunActivity, run: RunDetail): string {
  if (activity.phase === "queued") return "Waiting to start.";
  return outcomeCopy(activity, run).title;
}

export interface RunOutcomeCalloutProps {
  activity: RunActivity;
  run: RunDetail;
  /** "Retry from {step}": only with `runs.retry` and a step the server would retry. */
  retry?: { label: string; onRetry: () => void; busy?: boolean } | null;
  /**
   * Product gaps row 2: "Retry N tables", only with `runs.retry` and tables that did not land.
   * Offered on a failed and on a partial import (which has no failed step to retry).
   */
  retryTables?: { label: string; onOpen: () => void } | null;
  /**
   * Product gaps row 4: the page's action for a cause, and whether this reader may take it.
   * Absent, a cause block shows its fix and no button.
   */
  causes?: ImportCauseHandlers;
  /**
   * #1084: the cause's own action leads (`calloutFixFirst`), so the callout draws it as the
   * primary button and every retry as secondary. The page works it out once and passes the same
   * answer to its header retry; absent, the callout works it out itself.
   */
  fixFirst?: boolean;
  /**
   * #1230, #1200: why the page's Retry from failed step is off, drawn in the callout so a sighted
   * reader sees it next to the outcome, not only in the button's tooltip. Null when it is on, absent
   * or the outcome already says it.
   */
  retryOff?: string | null;
  /**
   * PR #1296 review: the run that holds a connection of this one while `retryOff` says so, linked
   * beside the sentence so the person can follow it. Null otherwise.
   */
  heldByRunId?: string | null;
}

/** The cause block, with the step's own text folded under Details when there is any. */
function causeMessage({
  found,
  causeAction,
  fixFirst,
  causes,
  details,
  detailsOpen,
  onToggleDetails,
}: {
  found: NonNullable<ReturnType<typeof calloutCause>>;
  causeAction: ReturnType<ImportCauseHandlers["action"]> | null;
  fixFirst: boolean;
  causes: ImportCauseHandlers | undefined;
  details: string;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <ImportCauseBlock
        cause={found.cause}
        action={causeAction}
        permitted={causes ? causes.permitted(found.cause) : false}
        {...(causes?.askLine ? { askLine: causes.askLine(found.cause) } : {})}
        actionPrimary={fixFirst}
        testId="run-outcome-cause"
      />
      {details ? (
        <div className="flex flex-col gap-1">
          <div>
            <SkyButton
              appearance="subtle"
              size="small"
              aria-expanded={detailsOpen}
              onClick={onToggleDetails}
              testId="run-outcome-details-toggle"
            >
              {detailsOpen ? "Hide details" : "Details"}
            </SkyButton>
          </div>
          {detailsOpen ? (
            // Verbatim: the runner already reduced this text (SEC-17).
            <SkyText size={200} color="muted" block testId="run-outcome-details">
              {details}
            </SkyText>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Retry from {step}" and "Retry N tables", each only when offered; nothing when neither is.
 * `secondary` (#1084, `fixBeforeRetry`): the cause's own action leads, so no retry is primary.
 */
function retryActions(
  stepRetry: RunOutcomeCalloutProps["retry"],
  tableRetry: RunOutcomeCalloutProps["retryTables"],
  secondary = false,
): ReactElement | undefined {
  if (!stepRetry && !tableRetry) return undefined;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {stepRetry ? (
        <SkyButton
          size="small"
          appearance={secondary ? "secondary" : "primary"}
          icon={RetryIcon}
          loading={stepRetry.busy}
          onClick={stepRetry.onRetry}
          testId="run-callout-retry"
        >
          {stepRetry.label}
        </SkyButton>
      ) : null}
      {tableRetry ? (
        <SkyButton
          size="small"
          appearance={stepRetry || secondary ? "secondary" : "primary"}
          icon={RetryIcon}
          onClick={tableRetry.onOpen}
          testId="run-callout-retry-tables"
        >
          {tableRetry.label}
        </SkyButton>
      ) : null}
    </div>
  );
}

/** The cause's own button in the callout, or null (a retry cause's would repeat the retry). */
function calloutCauseAction(
  found: ReturnType<typeof calloutCause>,
  causes: ImportCauseHandlers | undefined,
): ReturnType<ImportCauseHandlers["action"]> | null {
  return found && causes && found.cause.action !== "retry_failed_tables"
    ? causes.action(found.cause, found.dataflowId)
    : null;
}

/**
 * #1084: whether the run page leads with the shown cause's own action: a cause a retry cannot fix
 * until the customer acts (`fixBeforeRetry`), whose button is actually drawn for this reader (an
 * action the page can take, and the reader holds its key). Then that button is the page's one
 * primary action and every retry, in the callout and the header, is secondary. A reader who
 * cannot take the action (the ask line instead) keeps Retry primary, as before.
 */
export function calloutFixFirst(
  activity: RunActivity,
  run: RunDetail,
  causes: ImportCauseHandlers | undefined,
): boolean {
  const found = calloutCause(activity, run);
  if (!found || !causes) return false;
  return (
    calloutCauseAction(found, causes) !== null &&
    causes.permitted(found.cause) &&
    fixBeforeRetry(found.cause.source, found.cause.code, found.cause.action)
  );
}

export function RunOutcomeCallout({
  activity,
  run,
  retry,
  retryTables,
  causes,
  fixFirst: fixFirstProp,
  retryOff,
  heldByRunId,
}: RunOutcomeCalloutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Before paint and after every render: the library sets these again when the intent changes.
  useLayoutEffect(() => {
    ref.current?.removeAttribute("role");
    ref.current?.removeAttribute("aria-live");
  });
  const copy = outcomeCopy(activity, run);
  const showStepRetry = Boolean(retry) && activity.phase === "failed";
  const showTableRetry =
    Boolean(retryTables) && (activity.phase === "failed" || activity.phase === "partial");
  const found = calloutCause(activity, run);
  // A retry cause's button would repeat one the callout already draws.
  const causeAction = calloutCauseAction(found, causes);
  // #1084: the page's one rule, worked out once by the page (`fixFirst`); a host that passes none
  // gets the same answer here.
  const fixFirst = fixFirstProp ?? calloutFixFirst(activity, run, causes);
  const message = found
    ? causeMessage({
        found,
        causeAction,
        fixFirst,
        causes,
        details: copy.message,
        detailsOpen,
        onToggleDetails: () => setDetailsOpen((open) => !open),
      })
    : copy.message;
  const body = retryOff ? (
    <div className="flex min-w-0 flex-col gap-2">
      {typeof message === "string" ? <span>{message}</span> : message}
      <SkyText size={200} block testId="run-outcome-retry-off">
        {retryOff.startsWith("Retry ") ? retryOff : `Retry from failed step is off. ${retryOff}`}
        {heldByRunId ? (
          <>
            {" "}
            <SkyLink
              as={Link}
              href={absolutePath(`/runs/${heldByRunId}`)}
              testId="run-outcome-held-by"
            >
              Open that import
            </SkyLink>
            .
          </>
        ) : null}
      </SkyText>
    </div>
  ) : (
    message
  );
  return (
    <SkyMessageBar
      ref={ref}
      intent={copy.intent}
      layout="multiline"
      title={copy.title}
      message={body}
      actions={retryActions(
        showStepRetry ? retry : null,
        showTableRetry ? retryTables : null,
        fixFirst,
      )}
      testId="run-outcome-callout"
    />
  );
}
